import { useMutation } from "@apollo/client/react";
import { useModal } from "@ebay/nice-modal-react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerSection from "ds/components/Drawer/Section";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import useTypedContext from "hooks/useTypedContext";
import {
  BlueprintState,
  TemplateVersionUpdateInput,
  MutationTemplateVersionCreateArgs,
} from "types/generated";
import { getDrawerFormFix } from "utils/css";
import { stringIsRequired } from "utils/formValidators";
import { INITIAL_TEMPLATE } from "views/Account/TemplateVersion/constants";

import { REFETCH_QUERIES_LIST } from "../constants";
import { CREATE_VERSION, UPDATE_VERSION } from "../gql";
import { CreateVersionDrawerProps, CreateVersionFormFields } from "./types";

const CreateVersionDrawer = createDrawer(
  ({ version, templateId, isCloneMode }: CreateVersionDrawerProps) => {
    const drawer = useModal();

    const isEditMode = !!version && !isCloneMode;

    const navigate = useNavigate();

    const { onError, reportSuccess } = useTypedContext(FlashContext);

    const builderForm = useForm<CreateVersionFormFields>({
      defaultValues: {
        instructions: version?.instructions || "",
        state: isEditMode ? version?.state : BlueprintState.Draft,
        labels: version?.labels.map((value) => ({ value })) || [],
        versionNumber: version?.version || "",
        template: version?.rawTemplate || INITIAL_TEMPLATE,
      },
      mode: "onChange",
    });

    const {
      register,
      handleSubmit,
      formState: { errors, isDirty },
    } = builderForm;

    const [createVersion] = useMutation(CREATE_VERSION, {
      refetchQueries: REFETCH_QUERIES_LIST,
    });

    const [updateTemplate] = useMutation(UPDATE_VERSION, {
      refetchQueries: REFETCH_QUERIES_LIST,
    });

    const onCreateSubmit = (formData: CreateVersionFormFields) => {
      createVersion({
        variables: {
          input: {
            state: formData.state,
            labels: formData.labels.map((item) => item.value),
            versionNumber: formData.versionNumber,
            templateID: templateId,
            template: formData.template,
            instructions: formData.instructions,
          } as MutationTemplateVersionCreateArgs["input"],
        },
      })
        .then(({ data }) => {
          if (data?.templateVersionCreate) {
            reportSuccess({
              message: `Version "${data.templateVersionCreate.version}" successfully created`,
            });

            drawer.hide();
            navigate(`/template/${templateId}/version/${data.templateVersionCreate.id}`);
          }
        })
        .catch(onError);
    };

    const onEditSubmit = (formData: CreateVersionFormFields) => {
      if (version?.id) {
        updateTemplate({
          variables: {
            id: version.id,
            input: {
              state: formData.state,
              labels: formData.labels.map((item) => item.value),
              versionNumber: formData.versionNumber,
              template: formData.template,
              instructions: formData.instructions,
            } as TemplateVersionUpdateInput,
          },
        })
          .then(({ data }) => {
            if (data?.templateVersionUpdate) {
              reportSuccess({
                message: `Version "${data.templateVersionUpdate.version}" successfully saved`,
              });

              drawer.hide();
            }
          })
          .catch(onError);
      }
    };

    const onSubmit: SubmitHandler<CreateVersionFormFields> = (formData) => {
      if (isEditMode) {
        onEditSubmit(formData);
      } else {
        onCreateSubmit(formData);
      }
    };

    return (
      <DrawerForm isDirty={isDirty}>
        <FormProvider {...builderForm}>
          <form onSubmit={handleSubmit(onSubmit)} {...getDrawerFormFix()}>
            <DrawerHeader justify="between">
              {!isEditMode && !isCloneMode && <DrawerHeaderTitle title="Create version" />}
              {isEditMode && <DrawerHeaderTitle title="Edit version" />}
              {isCloneMode && <DrawerHeaderTitle title="Duplicate version" />}
              <DrawerCloseIcon />
            </DrawerHeader>
            <DrawerBody noPadding>
              <DrawerSection>
                <FormField
                  label="Unique version identifier"
                  error={errors?.versionNumber?.message}
                  tooltipInfo="A unique identifier for this version, e.g., 1.0.0"
                >
                  {({ ariaInputProps }) => (
                    <Input
                      placeholder="0.0.1"
                      error={!!errors?.versionNumber}
                      {...register("versionNumber", {
                        validate: stringIsRequired("Version identifier is required."),
                      })}
                      {...ariaInputProps}
                    />
                  )}
                </FormField>
                <FormField label="Deployment instruction" isOptional helperText="Supports Markdown">
                  {({ ariaInputProps }) => (
                    <Textarea
                      placeholder="Instructions for deploying this version..."
                      {...register("instructions")}
                      {...ariaInputProps}
                    />
                  )}
                </FormField>
              </DrawerSection>

              <DrawerFooter>
                <DrawerFooterActions>
                  <DrawerCancelButton />
                  <Button variant="primary" type="submit">
                    {isEditMode ? "Save" : "Create"}
                  </Button>
                </DrawerFooterActions>
              </DrawerFooter>
            </DrawerBody>
          </form>
        </FormProvider>
      </DrawerForm>
    );
  }
);

export const showCreateVersionDrawer = createDrawerTrigger(CreateVersionDrawer);
